/**
 * Curator Page — Dev-only entity management UI (v2)
 * 
 * Features:
 * - Entity type tabs on the left
 * - Category-grouped sections with entity counts
 * - Card layout showing name + description + status
 * - Multi-select with checkboxes
 * - Bulk archive/restore
 * - Status filter + name search
 * - Keyboard shortcuts (/ = search, Ctrl+A = select all)
 * 
 * Gated behind DevMode (Ctrl+Shift+D)
 */

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useDevMode } from '../contexts/DevModeContext';
import {
    Archive, RotateCcw, Search, CheckSquare, Square, Loader2,
    AlertTriangle, RefreshCw, X, ChevronRight, ChevronDown, Filter
} from 'lucide-react';
import AuditPanel from '../components/AuditPanel';

const API_BASE = import.meta.env.VITE_API_URL
    ? `${import.meta.env.VITE_API_URL}/api`
    : '/api';

const ENTITY_TYPES = [
    { value: 'kinks', label: 'Kinks', icon: '🔥' },
    { value: 'sex_actions', label: 'Actions', icon: '⚡' },
    { value: 'arousal_mechanics', label: 'Arousal Mechanics', icon: '🧠' },
    { value: 'arousal_effects', label: 'Arousal Effects', icon: '💫' },
    { value: 'archetypes', label: 'Archetypes', icon: '🎭' },
    { value: 'activities', label: 'Activities', icon: '🎲' },
    { value: 'dynamics', label: 'Dynamics', icon: '⚖️' },
    { value: 'gear', label: 'Gear', icon: '🔧' },
    { value: 'scenarios', label: 'Scenarios', icon: '🎬' },
    { value: 'positions', label: 'Positions', icon: '🤸' },
    { value: 'body_parts', label: 'Body Parts', icon: '🫦' },
    { value: 'characters', label: 'Characters', icon: '👤' },
];

async function curatorFetch(endpoint, body) {
    const res = await fetch(`${API_BASE}/agent/curator/${endpoint}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
    });
    if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || `HTTP ${res.status}`);
    }
    return res.json();
}

// ─── Status badge ───────────────────────────────────────────────────────────
function StatusDot({ status }) {
    const s = status || 'empty';
    const colors = {
        archived: 'bg-red-500',
        generated: 'bg-emerald-500',
        empty: 'bg-slate-500',
        published: 'bg-blue-500',
    };
    return (
        <span className="inline-flex items-center gap-1.5 text-[11px] font-medium text-slate-400">
            <span className={`w-2 h-2 rounded-full ${colors[s] || colors.empty}`} />
            {s}
        </span>
    );
}

// ─── Category section with collapsible header ───────────────────────────────
function CategoryGroup({ category, entities, selected, toggleSelect, collapsed, onToggle }) {
    const count = entities.length;
    const selectedInGroup = entities.filter(e => selected.has(e.id)).length;

    return (
        <div className="mb-1">
            {/* Category header */}
            <button
                onClick={onToggle}
                className="w-full flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-white/[0.04] transition-colors group"
            >
                {collapsed
                    ? <ChevronRight className="w-4 h-4 text-slate-500 group-hover:text-slate-300 transition-colors" />
                    : <ChevronDown className="w-4 h-4 text-slate-500 group-hover:text-slate-300 transition-colors" />
                }
                <span className="text-sm font-semibold text-slate-300">{category}</span>
                <span className="text-xs text-slate-600 tabular-nums">{count}</span>
                {selectedInGroup > 0 && (
                    <span className="text-xs text-amber-500 ml-auto tabular-nums">{selectedInGroup} selected</span>
                )}
            </button>

            {/* Entity cards */}
            {!collapsed && (
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-1.5 px-2 pb-3">
                    {entities.map(entity => (
                        <div
                            key={entity.id}
                            onClick={() => toggleSelect(entity.id)}
                            className={`
                flex gap-2.5 p-2.5 rounded-lg cursor-pointer transition-all border
                ${selected.has(entity.id)
                                    ? 'bg-amber-500/10 border-amber-500/30 ring-1 ring-amber-500/20'
                                    : 'bg-white/[0.02] border-white/[0.04] hover:bg-white/[0.05] hover:border-white/[0.08]'
                                }
                ${entity.populationStatus === 'archived' ? 'opacity-50' : ''}
              `}
                        >
                            {/* Checkbox */}
                            <div className="pt-0.5 shrink-0">
                                {selected.has(entity.id)
                                    ? <CheckSquare className="w-4 h-4 text-amber-400" />
                                    : <Square className="w-4 h-4 text-slate-700" />
                                }
                            </div>

                            {/* Content */}
                            <div className="min-w-0 flex-1">
                                <div className="flex items-center gap-2 mb-0.5">
                                    <span className="font-medium text-sm text-white truncate">{entity.name}</span>
                                    <StatusDot status={entity.populationStatus} />
                                </div>
                                {(entity.summary || entity.description) && (
                                    <p className="text-xs text-slate-500 leading-relaxed line-clamp-2">
                                        {entity.summary || entity.description}
                                    </p>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

// ─── Main Curator Page ──────────────────────────────────────────────────────
export default function CuratorPage() {
    const { isDevMode } = useDevMode();
    const [entityType, setEntityType] = useState('kinks');
    const [showAudit, setShowAudit] = useState(false);
    const [statusFilter, setStatusFilter] = useState('active');
    const [search, setSearch] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [entities, setEntities] = useState([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [selected, setSelected] = useState(new Set());
    const [actionLoading, setActionLoading] = useState(false);
    const [toast, setToast] = useState(null);
    const [offset, setOffset] = useState(0);
    const [collapsedCategories, setCollapsedCategories] = useState(new Set());
    const LIMIT = 500;
    const searchRef = useRef(null);

    // Debounce search
    useEffect(() => {
        const t = setTimeout(() => setDebouncedSearch(search), 300);
        return () => clearTimeout(t);
    }, [search]);

    // Fetch entities
    const fetchEntities = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const data = await curatorFetch('list', {
                entityType,
                status: statusFilter === 'all' ? undefined : statusFilter,
                search: debouncedSearch || undefined,
                limit: LIMIT,
                offset,
            });
            setEntities(data.entities);
            setTotal(data.total);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }, [entityType, statusFilter, debouncedSearch, offset]);

    useEffect(() => {
        setOffset(0);
        setSelected(new Set());
        setCollapsedCategories(new Set());
    }, [entityType, statusFilter, debouncedSearch]);

    useEffect(() => { fetchEntities(); }, [fetchEntities]);

    // Group entities by category
    const grouped = useMemo(() => {
        const groups = new Map();
        entities.forEach(e => {
            const cat = e.category || 'Uncategorized';
            if (!groups.has(cat)) groups.set(cat, []);
            groups.get(cat).push(e);
        });
        return [...groups.entries()];
    }, [entities]);

    // Toast
    const showToast = useCallback((msg, type = 'info') => {
        setToast({ msg, type });
        setTimeout(() => setToast(null), 3000);
    }, []);

    // Select
    const toggleSelect = useCallback((id) => {
        setSelected(prev => {
            const next = new Set(prev);
            next.has(id) ? next.delete(id) : next.add(id);
            return next;
        });
    }, []);

    const selectAll = useCallback(() => {
        if (selected.size === entities.length) {
            setSelected(new Set());
        } else {
            setSelected(new Set(entities.map(e => e.id)));
        }
    }, [entities, selected.size]);

    const selectCategory = useCallback((catEntities) => {
        setSelected(prev => {
            const next = new Set(prev);
            const allSelected = catEntities.every(e => next.has(e.id));
            catEntities.forEach(e => {
                allSelected ? next.delete(e.id) : next.add(e.id);
            });
            return next;
        });
    }, []);

    // Bulk actions
    const bulkArchive = useCallback(async () => {
        if (selected.size === 0) return;
        setActionLoading(true);
        try {
            const data = await curatorFetch('bulk-archive', { entityType, entityIds: [...selected] });
            showToast(`✓ Archived ${data.archived} items`, 'success');
            setSelected(new Set());
            fetchEntities();
        } catch (err) { showToast(`✗ ${err.message}`, 'error'); }
        finally { setActionLoading(false); }
    }, [selected, entityType, fetchEntities, showToast]);

    const bulkRestore = useCallback(async () => {
        if (selected.size === 0) return;
        setActionLoading(true);
        try {
            const data = await curatorFetch('bulk-restore', { entityType, entityIds: [...selected] });
            showToast(`✓ Restored ${data.restored} items`, 'success');
            setSelected(new Set());
            fetchEntities();
        } catch (err) { showToast(`✗ ${err.message}`, 'error'); }
        finally { setActionLoading(false); }
    }, [selected, entityType, fetchEntities, showToast]);

    // Toggle category collapse
    const toggleCategory = useCallback((cat) => {
        setCollapsedCategories(prev => {
            const next = new Set(prev);
            next.has(cat) ? next.delete(cat) : next.add(cat);
            return next;
        });
    }, []);

    // Keyboard shortcuts
    useEffect(() => {
        const handler = (e) => {
            if (e.key === '/' && e.target.tagName !== 'INPUT') {
                e.preventDefault();
                searchRef.current?.focus();
            }
            if ((e.ctrlKey || e.metaKey) && e.key === 'a' && e.target.tagName !== 'INPUT') {
                e.preventDefault();
                selectAll();
            }
        };
        window.addEventListener('keydown', handler);
        return () => window.removeEventListener('keydown', handler);
    }, [selectAll]);

    if (!isDevMode) {
        return (
            <div className="min-h-screen flex items-center justify-center text-slate-500">
                <div className="text-center">
                    <AlertTriangle className="w-12 h-12 mx-auto mb-4 opacity-50" />
                    <p className="text-lg">Dev mode required</p>
                    <p className="text-sm mt-2 opacity-60">Press Ctrl+Shift+D to enable</p>
                </div>
            </div>
        );
    }

    const currentEntityLabel = ENTITY_TYPES.find(t => t.value === entityType)?.label || entityType;

    return (
        <div className="min-h-screen bg-[#0a0a0f] text-slate-200 flex">
            {/* Toast */}
            {toast && (
                <div className={`fixed top-20 right-4 z-50 px-4 py-2.5 rounded-lg shadow-2xl text-sm font-medium
          ${toast.type === 'success' ? 'bg-emerald-600 text-white' :
                        toast.type === 'error' ? 'bg-red-600 text-white' : 'bg-slate-700 text-slate-200'}`}
                >
                    {toast.msg}
                </div>
            )}

            {/* ─── Left Sidebar: Entity Types ─── */}
            <div className="w-52 shrink-0 bg-[#0d0d14] border-r border-white/[0.04] sticky top-16 h-[calc(100vh-4rem)] overflow-y-auto flex flex-col">
                <div className="p-3 border-b border-white/[0.04]">
                    <h1 className="text-base font-bold text-amber-400 tracking-tight">Curator</h1>
                    <p className="text-[11px] text-slate-600 mt-0.5">Entity management</p>
                </div>
                <nav className="p-1.5 space-y-0.5 flex-1">
                    {ENTITY_TYPES.map(t => (
                        <button
                            key={t.value}
                            onClick={() => { setEntityType(t.value); setShowAudit(false); }}
                            className={`w-full text-left px-2.5 py-2 rounded-md text-sm flex items-center gap-2 transition-all
                ${entityType === t.value && !showAudit
                                    ? 'bg-amber-500/15 text-amber-300 font-medium'
                                    : 'text-slate-400 hover:text-slate-200 hover:bg-white/[0.04]'
                                }`}
                        >
                            <span className="text-base leading-none">{t.icon}</span>
                            <span className="truncate">{t.label}</span>
                        </button>
                    ))}
                </nav>
                {/* Audit tab at bottom */}
                <div className="p-1.5 border-t border-white/[0.04]">
                    <button
                        onClick={() => setShowAudit(true)}
                        className={`w-full text-left px-2.5 py-2 rounded-md text-sm flex items-center gap-2 transition-all
                ${showAudit
                                ? 'bg-amber-500/15 text-amber-300 font-medium'
                                : 'text-slate-400 hover:text-slate-200 hover:bg-white/[0.04]'
                            }`}
                    >
                        <Filter className="w-4 h-4" />
                        <span>Audit</span>
                    </button>
                </div>
            </div>

            {/* ─── Main Content ─── */}
            <div className="flex-1 min-w-0">
                {showAudit ? (
                    <AuditPanel showToast={showToast} />
                ) : (
                    <>
                        {/* Top toolbar */}
                        <div className="sticky top-16 z-30 bg-[#0a0a0f]/95 backdrop-blur-md border-b border-white/[0.04]">
                            <div className="px-5 py-3 flex items-center gap-3">
                                {/* Status filter pills */}
                                <div className="flex gap-0.5 bg-slate-800/50 rounded-lg p-0.5">
                                    {[
                                        { value: 'active', label: 'Active', bg: 'bg-emerald-600' },
                                        { value: 'archived', label: 'Archived', bg: 'bg-red-600' },
                                        { value: 'all', label: 'All', bg: 'bg-slate-600' },
                                    ].map(s => (
                                        <button
                                            key={s.value}
                                            onClick={() => setStatusFilter(s.value)}
                                            className={`px-3 py-1 text-xs font-medium rounded-md transition-all
                    ${statusFilter === s.value
                                                    ? `${s.bg} text-white shadow-sm`
                                                    : 'text-slate-400 hover:text-white hover:bg-white/5'
                                                }`}
                                        >
                                            {s.label}
                                        </button>
                                    ))}
                                </div>

                                {/* Search */}
                                <div className="relative flex-1 max-w-sm">
                                    <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                                    <input
                                        ref={searchRef}
                                        type="text"
                                        placeholder="Search names… ( / )"
                                        value={search}
                                        onChange={e => setSearch(e.target.value)}
                                        className="w-full pl-8 pr-8 py-1.5 text-sm rounded-lg bg-slate-800/80 border border-white/10 text-white placeholder-slate-500 focus:border-amber-500 focus:outline-none transition-colors"
                                    />
                                    {search && (
                                        <button onClick={() => setSearch('')} className="absolute right-2 top-1/2 -translate-y-1/2">
                                            <X className="w-4 h-4 text-slate-500 hover:text-white" />
                                        </button>
                                    )}
                                </div>

                                {/* Stats */}
                                <div className="ml-auto flex items-center gap-3 text-xs text-slate-500 tabular-nums">
                                    <span>{total.toLocaleString()} {currentEntityLabel.toLowerCase()}</span>
                                    {selected.size > 0 && (
                                        <span className="text-amber-400 font-medium">{selected.size} selected</span>
                                    )}
                                </div>

                                {/* Refresh */}
                                <button onClick={fetchEntities} className="p-1.5 rounded-md hover:bg-white/5 text-slate-400 hover:text-white transition-colors">
                                    <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                                </button>
                            </div>

                            {/* Action bar (visible when items selected) */}
                            {selected.size > 0 && (
                                <div className="px-5 pb-3">
                                    <div className="flex items-center gap-3 py-2 px-3 bg-amber-500/10 border border-amber-500/20 rounded-lg">
                                        <span className="text-sm font-medium text-amber-400">{selected.size} selected</span>
                                        <div className="h-4 w-px bg-white/10" />
                                        {statusFilter !== 'archived' && (
                                            <button
                                                onClick={bulkArchive}
                                                disabled={actionLoading}
                                                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md bg-red-600/80 text-white hover:bg-red-600 transition-colors disabled:opacity-50"
                                            >
                                                {actionLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Archive className="w-3 h-3" />}
                                                Archive Selected
                                            </button>
                                        )}
                                        {statusFilter !== 'active' && (
                                            <button
                                                onClick={bulkRestore}
                                                disabled={actionLoading}
                                                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md bg-emerald-600/80 text-white hover:bg-emerald-600 transition-colors disabled:opacity-50"
                                            >
                                                {actionLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : <RotateCcw className="w-3 h-3" />}
                                                Restore Selected
                                            </button>
                                        )}
                                        <button
                                            onClick={() => setSelected(new Set())}
                                            className="ml-auto px-2 py-1 text-xs text-slate-400 hover:text-white rounded hover:bg-white/5 transition-colors"
                                        >
                                            Clear selection
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Content area */}
                        <div className="px-5 py-4">
                            {error && (
                                <div className="p-4 mb-4 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400 text-sm">
                                    {error}
                                </div>
                            )}

                            {loading && entities.length === 0 ? (
                                <div className="py-20 text-center text-slate-500">
                                    <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" />
                                    Loading {currentEntityLabel.toLowerCase()}…
                                </div>
                            ) : entities.length === 0 ? (
                                <div className="py-20 text-center text-slate-500">
                                    <p>No {currentEntityLabel.toLowerCase()} found</p>
                                    <p className="text-xs mt-1 opacity-60">
                                        {statusFilter === 'archived' ? 'Nothing archived in this table' : 'Try a different search or filter'}
                                    </p>
                                </div>
                            ) : (
                                <div className="space-y-1">
                                    {grouped.map(([category, catEntities]) => (
                                        <CategoryGroup
                                            key={category}
                                            category={category}
                                            entities={catEntities}
                                            selected={selected}
                                            toggleSelect={toggleSelect}
                                            collapsed={collapsedCategories.has(category)}
                                            onToggle={() => toggleCategory(category)}
                                        />
                                    ))}
                                </div>
                            )}

                            {/* Pagination */}
                            {total > LIMIT && (
                                <div className="flex items-center justify-center gap-4 py-6 text-sm text-slate-400">
                                    <button
                                        onClick={() => setOffset(Math.max(0, offset - LIMIT))}
                                        disabled={offset === 0}
                                        className="px-4 py-1.5 rounded bg-slate-800 hover:bg-slate-700 disabled:opacity-30 transition-colors"
                                    >
                                        ← Prev
                                    </button>
                                    <span className="tabular-nums">
                                        {offset + 1}–{Math.min(offset + LIMIT, total)} of {total}
                                    </span>
                                    <button
                                        onClick={() => setOffset(offset + LIMIT)}
                                        disabled={offset + LIMIT >= total}
                                        className="px-4 py-1.5 rounded bg-slate-800 hover:bg-slate-700 disabled:opacity-30 transition-colors"
                                    >
                                        Next →
                                    </button>
                                </div>
                            )}
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}
